/**
 * Determines if the user should be redirected to the onboarding wizard.
 * Checks both localStorage (immediate) and backend preferences.
 *
 * Pre-existing users (who have courses but no onboarding preference) are
 * treated as already completed — they must explicitly click "Restart
 * Onboarding" on the profile page to see it again.
 *
 * @param {{ user, uiPreferences, hasCourses?: boolean }} params
 * @returns {boolean} true if user needs onboarding
 */
export function shouldShowOnboarding({ user, uiPreferences, hasCourses = false }) {
  if (!user) return false;

  const userId = user.userId || user.id;
  if (!userId) return false;

  // Check localStorage first (fast, no network)
  const localKey = `dh_onboarding_complete:${userId}`;
  if (localStorage.getItem(localKey) === '1') return false;

  // Check backend preferences
  if (uiPreferences?.onboarding_v2_completed === true) {
    // Sync to localStorage for next time
    try { localStorage.setItem(localKey, '1'); } catch (e) { /* */ }
    return false;
  }

  // Explicitly set to false via "Restart Onboarding" → always show
  if (uiPreferences?.onboarding_v2_completed === false) {
    return true;
  }

  // Preference key is absent/undefined.
  // Pre-existing users who already have courses should NOT see onboarding.
  if (hasCourses) return false;

  // Truly new user with no courses and no preference → show onboarding
  return true;
}

/**
 * Mark onboarding as completed (localStorage only — backend is handled by the wizard).
 */
export function markOnboardingComplete(userId) {
  try {
    localStorage.setItem(`dh_onboarding_complete:${userId}`, '1');
  } catch (e) { /* */ }
}
